import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Flex,
  Heading,
  Icon,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiChevronRight, FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  SourcingEventStatus,
  type SourcingEventV2Dto,
  useBeginEvaluationMutation,
  useListBidV2SForEventQuery,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../../../../urls";
import { effectiveBidDeadline } from "../stage";
import { isBidDeadlinePassed } from "../../supplierRows";
import {
  deriveTenderQualification,
  type TenderQualificationRow,
  type TenderQualificationSummary,
} from "./deriveTenderQualification";

interface Props {
  event: SourcingEventV2Dto;
}

/**
 * Kvalifisering for single-stage procedures: the buyer passes or rejects every delivered tender
 * before the evaluation opens. Unlike the two-stage flow this ends in «Fullfør kvalifisering»,
 * not an invitation to tender — the tenders are already in.
 */
export const TenderQualificationTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const deadlinePassed = isBidDeadlinePassed(event);
  const { data, isLoading } = useListBidV2SForEventQuery({ eventId: event.id }, { skip: !deadlinePassed });

  const tenders = useMemo(
    () => (data?.bids ?? []).filter((bid) => bid.forNegotiationRound == null),
    [data],
  );
  const summary = useMemo(
    () => deriveTenderQualification(event.eventData.selectionCriteria ?? [], tenders),
    [event.eventData.selectionCriteria, tenders],
  );

  if (!deadlinePassed) {
    const deadline = effectiveBidDeadline(event);
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10">
          <Flex direction="column" align="center" gap="3" textAlign="center">
            <Icon as={FiClock} boxSize="8" color="smPrimary" />
            <Text fontWeight="semibold" color="smText">
              {t("The competition is open for tenders")}
            </Text>
            {deadline && (
              <Text fontSize="sm" color="smMuted">
                {t("Deadline for tenders")}: <b>{displayDateWithTime(deadline)}</b>
              </Text>
            )}
            <Text fontSize="xs" color="smMuted" maxW="md">
              {t("Qualification opens when the deadline has passed and the tenders are unsealed.")}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  const qualificationOpen = event.status === SourcingEventStatus.Published;

  return (
    <Stack spacing="5">
      {!qualificationOpen && <CompletedBanner summary={summary} />}

      {summary.rows.length === 0 ? (
        <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
          <CardBody py="10" textAlign="center">
            <Text color="smMuted">{t("No tenders were received.")}</Text>
          </CardBody>
        </Card>
      ) : (
        <Box>
          <Heading as="h3" size="sm" color="smText" mb="2">
            {summary.pending > 0
              ? t("{{resolved}} of {{total}} suppliers qualified or rejected", {
                  resolved: summary.rows.length - summary.pending,
                  total: summary.rows.length,
                })
              : t("{{qualified}} qualified, {{rejected}} rejected", {
                  qualified: summary.qualified,
                  rejected: summary.rejected,
                })}
          </Heading>
          <TenderTable eventId={event.id} rows={summary.rows} />
        </Box>
      )}

      {qualificationOpen && <CompleteQualificationCard event={event} summary={summary} />}
    </Stack>
  );
};

const CompletedBanner = ({ summary }: { summary: TenderQualificationSummary }) => {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      gap="3"
      bg="green.50"
      border="1px solid"
      borderColor="green.200"
      rounded="xl"
      px="5"
      py="4"
      color="green.800"
    >
      <Icon as={FiCheckCircle} boxSize="5" />
      <Box>
        <Text fontWeight="semibold">{t("Qualification is complete")}</Text>
        <Text fontSize="sm" opacity={0.85}>
          {t("{{qualified}} qualified, {{rejected}} rejected", {
            qualified: summary.qualified,
            rejected: summary.rejected,
          })}
        </Text>
      </Box>
    </Flex>
  );
};

const CompleteQualificationCard = ({
  event,
  summary,
}: {
  event: SourcingEventV2Dto;
  summary: TenderQualificationSummary;
}) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const [beginEvaluation, { isLoading }] = useBeginEvaluationMutation();

  const complete = async () => {
    try {
      await beginEvaluation({ eventId: event.id }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  // The action is anchored left, clear of the Intercom launcher that owns the bottom-right corner.
  return (
    <Stack
      spacing="3"
      align="flex-start"
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Box>
        <Heading size="sm" mb="1">
          {t("Complete qualification")}
        </Heading>
        <Text fontSize="sm" color="smMuted">
          {summary.rows.length === 0
            ? t("No tenders were received — the evaluation cannot be opened.")
            : summary.pending > 0
              ? t("Qualify or reject the remaining {{count}} suppliers first.", {
                  count: summary.pending,
                })
              : t("Completing the qualification opens the evaluation of the tenders.")}
        </Text>
      </Box>
      <Button
        colorScheme="teal"
        onClick={complete}
        isLoading={isLoading}
        isDisabled={!summary.isResolved}
      >
        {t("Complete qualification")}
      </Button>
    </Stack>
  );
};

const TenderTable = ({ eventId, rows }: { eventId: string; rows: TenderQualificationRow[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
      <Table size="md">
        <Thead>
          <Tr>
            <Th>{t("Supplier")}</Th>
            <Th>{t("Requirements reviewed")}</Th>
            <Th>{t("Status")}</Th>
            <Th aria-hidden />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={row.bidId}
              cursor="pointer"
              _hover={{ bg: "smBackgroundSecondary" }}
              // Straight to the requirements: reviewing them is what the row is asking for.
              onClick={() =>
                navigate(`${urls.v2.sourcingEvents.bidDetail.go(eventId, row.bidId)}?tab=qualifications`)
              }
            >
              <Td>
                <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
                  {row.supplierName}
                </Text>
              </Td>
              <Td
                fontSize="sm"
                color={row.reviewed === row.total ? "smText" : "smMuted"}
                fontFamily="mono"
              >
                {row.total > 0 ? `${row.reviewed}/${row.total}` : "–"}
              </Td>
              <Td>
                <StateBadge state={row.state} />
              </Td>
              <Td textAlign="right">
                <Icon as={FiChevronRight} color="smMuted" />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const StateBadge = ({ state }: { state: TenderQualificationRow["state"] }) => {
  const { t } = useTranslation();
  if (state === "QUALIFIED") {
    return (
      <Badge colorScheme="green" fontSize="xs">
        {t("Qualified")}
      </Badge>
    );
  }
  if (state === "REJECTED") {
    return (
      <Badge colorScheme="red" fontSize="xs">
        {t("Avvist")}
      </Badge>
    );
  }
  return (
    <Badge colorScheme="gray" fontSize="xs">
      {t("Under review")}
    </Badge>
  );
};
